import React, { useEffect, useState } from 'react'
import { cn } from '@utils/safeTs'
import styles from './EditorSidebarText.module.sass'
import EditorSidebarElement from '../EditorSidebarElement/EditorSidebarElement'
import { EditorColoreApiType, EditorTextType } from '@utils/types'
import EditorColor from '@components/atoms/EditorColor/EditorColor'
import EditorText from '@components/atoms/EditorText/EditorText'
import { FormField, FormInput, FormSelect, FormTextarea } from '@components/atoms'
import useTrans from '@hooks/useTrans'
import { Italic, Underline } from '@components/icons'
import { convertFontSize, parseFontSize } from '@utils/editor-bozze'

interface Props {
  text?: EditorTextType
  selected?: boolean
  onRemove?: () => void
  onColorChange?: (id: number, o: string, n: string) => void
  onObjectAction?: (id: string | number, action: string, payload?: any) => void
  colorLimits?: {
    allowedColors?: EditorColoreApiType[]
    lockColorChange?: EditorColoreApiType | null
  }
  className?: string
}

const EditorSidebarText = (props: Props) => {
  const {
    text,
    selected = false,
    onRemove,
    onColorChange,
    onObjectAction,
    colorLimits,
    className = '',
  } = props

  const t = useTrans()
  const [fontSizeDraft, setFontSizeDraft] = useState('')

  useEffect(() => {
    if (!text?.id) {
      setFontSizeDraft('')
      return
    }

    setFontSizeDraft(String(parseFontSize(text.fontSize) || ''))
  }, [text?.id, text?.fontSize])

  const familyOpts = [
    'Arial',
    'Verdana',
    'Helvetica',
    'Tahoma',
    'Trebuchet MS',
    'Times New Roman',
    'Georgia',
    'Garamond',
    'Courier New',
    'Lucida Console',
    'Monaco',
    'Brush Script MT',
    'cursive',
    'fantasy',
    'sans-serif',
    'serif', // Generic families
  ].map((f) => ({ label: f, value: f }))

  const weightOpts = [
    // TODO - check dei font weights (dipendono dal fontFamily selezionato?)
    { label: 'Normal', value: '400' },
    { label: 'Bold', value: '700' },
    { label: '100', value: '100' },
    { label: '200', value: '200' },
    { label: '300', value: '300' },
    { label: '400', value: '400' },
    { label: '500', value: '500' },
    { label: '600', value: '600' },
    { label: '700', value: '700' },
    { label: '800', value: '800' },
    { label: '900', value: '900' },
  ]

  return text ? (
    <EditorSidebarElement
      selected={selected}
      className={cn(styles.root, className)}
      onClose={onRemove}
    >
      <EditorText text={text} onClose={onRemove} />
      <div className={styles.fields}>
        <FormField styleVariant="editor" className={cn(styles.field, styles['field--text'])}>
          <FormTextarea
            id="editortextsidebar_new_text"
            placeholder={t('Inserisci il testo')}
            value={text.text}
            onChange={(e) => {
              onObjectAction &&
                onObjectAction(text.id, 'property', { name: 'text', value: e.target.value })
            }}
          />
        </FormField>
        <FormField
          styleVariant="editor"
          size="sm"
          label={t('Family')}
          className={cn(styles.field, styles['field--family'])}
        >
          <FormSelect
            id={`editorsidebartext_${text.id}_family`}
            value={text.fontFamily}
            options={familyOpts}
            onChange={(e) => {
              onObjectAction &&
                onObjectAction(text.id, 'property', { name: 'fontFamily', value: e.target.value })
            }}
          />
        </FormField>
        <FormField
          styleVariant="editor"
          size="sm"
          label={t('Size')}
          className={cn(styles.field, styles['field--size'])}
        >
          <FormInput
            id={`editorsidebartext_${text.id}_size`}
            value={fontSizeDraft}
            onChange={(e) => {
              setFontSizeDraft(e.target.value)
            }}
            onBlur={(e) => {
              onObjectAction &&
                onObjectAction(text.id, 'property', {
                  name: 'fontSize',
                  value: convertFontSize(e.target.value),
                })
            }}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return
              e.currentTarget.blur()
            }}
          />
        </FormField>
        <FormField
          styleVariant="editor"
          size="sm"
          label={t('Weight')}
          className={cn(styles.field, styles['field--weight'])}
        >
          <FormSelect
            id={`editorsidebartext_${text.id}_weight`}
            value={text.fontWeight}
            options={weightOpts}
            onChange={(e) => {
              onObjectAction &&
                onObjectAction(text.id, 'property', {
                  name: 'fontWeight',
                  value: e.target.value,
                })
            }}
          />
        </FormField>
        <div className={styles.buttonFields}>
          <button
            type="button"
            onClick={() => {
              onObjectAction &&
                onObjectAction(text.id, 'property', { name: 'underline', value: !text.underline })
            }}
            className={cn(
              styles.buttonField,
              text.underline ? styles['buttonField--selected'] : '',
              styles['buttonField--underline']
            )}
          >
            <Underline />
          </button>
          <button
            type="button"
            onClick={() => {
              onObjectAction &&
                onObjectAction(text.id, 'property', {
                  name: 'fontStyle',
                  value: text.fontStyle === 'italic' ? 'normal' : 'italic',
                })
            }}
            className={cn(
              styles.buttonField,
              text.fontStyle === 'italic' ? styles['buttonField--selected'] : '',
              styles['buttonField--italic']
            )}
          >
            <Italic />
          </button>
        </div>
        {!!text.colore && (
          <EditorColor
            id={`editorsidebartext_${text.id}_color`}
            variant={text.colore.variant}
            value={text.colore.value}
            onChange={(colorValue) => {
              onColorChange && onColorChange(text.id, text.colore.value, colorValue)
            }}
            allowedColors={colorLimits?.allowedColors}
            lockColorChange={colorLimits?.lockColorChange}
            className={styles.color}
          />
        )}
      </div>
    </EditorSidebarElement>
  ) : (
    <></>
  )
}

export default EditorSidebarText
